<template>
  <CCard>
    <CCardHeader class="d-flex align-items-center">
      <CIcon
        name="cil-settings"
        class="mr-2"
      />
      <strong>Bid Settings</strong>
    </CCardHeader>

    <CCardBody>
      <CForm
        novalidate
        @submit.prevent
      >
        <CRow
          form
          class="form-group"
        >
          <CCol
            tag="label"
            sm="3"
            class="col-form-label"
          >
            Override Bid Expiration
          </CCol>
          <CCol
            sm="9"
            class="mt-1"
          >
            <CSwitch
              shape="pill"
              :checked.sync="overrideBidExpiration"
              color="primary"
              @update:checked="onToggleOverrideBidExpiration"
            />
          </CCol>
        </CRow>

        <CInput
          v-if="overrideBidExpiration"
          v-model.trim="$v.bidPreferences.bidExpirationInSeconds.$model"
          type="number"
          label="Bid Expiration (in seconds)"
          horizontal
          :is-valid="checkIfValid(['bidPreferences', 'bidExpirationInSeconds'])"
          :invalid-feedback="cannotBeBlank('Bid expiration')"
        /> 

        <RateLimitInput
          v-model="bidPreferences.rateLimit"
          :submitted="submitted"
          @validity-change="rateLimitValid = $event"
          @input="$v.$touch()"
        />

        <div class="form-row">
          <CCol
            :md="{ offset: '3', size: '9' }"
            :sm="{ offset: '3', size: '9' }"
          >
            <CButton
              type="submit"
              size="sm"
              color="primary"
              :disabled="!isFormEdited"
              @click="onFormSubmit"
            >
              Change
            </CButton>
          </CCol>
        </div>
      </CForm>
    </CCardBody>

    <CModal
      title="Confirm bid preferences change"
      color="success"
      :show.sync="warningBidPreferencesChangeModal"
    >
      Are you sure you want to change the bid preferences?

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isLoading"
          @click="onConfirmSubmit"
        >
          Change
        </ButtonLoading>

        <CButton
          color="danger"
          @click="warningBidPreferencesChangeModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </CCard>
</template>

<script>
import { minValue, requiredIf } from 'vuelidate/lib/validators'
import _ from 'lodash'
import { mapActions } from 'vuex'

import formMixins from '@/mixins/form-mixins'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import RateLimitInput from '@/components/common/RateLimitInput.vue'

export default {
  name: 'SellerBidSettings',

  components: {
    RateLimitInput
  },

  mixins: [formMixins],

  props: {
    accountId: {
      type: String,
      default: ''
    },

    existingBidPreferences: {
      type: Object,
      default() {
        return {}
      }
    }
  },

  validations() {
    return {
      bidPreferences: {
        bidExpirationInSeconds: {
          requiredIf: requiredIf(() => {
            return this.bidPreferences.bidExpirationInSeconds != null
          }),
          minValue: minValue(1)
        }
      }
    }
  },

  data() {
    return {
      bidPreferences: {
        bidExpirationInSeconds: null,

        rateLimit: null
      },
      overrideBidExpiration: false,
      rateLimitValid: true,

      warningBidPreferencesChangeModal: false,
      isLoading: false
    }
  },

  watch: {
    existingBidPreferences: {
      immediate: true,
      handler(newValue) {
        this.bidPreferences = _.cloneDeep(newValue)

        this.handleBidPreferencesChange()
      }
    }
  },

  methods: {
    ...mapActions('accounts', [
      'updateSellerBidSettings'
    ]),

    handleBidPreferencesChange() {
      if (this.bidPreferences.bidExpirationInSeconds != null) {
        this.overrideBidExpiration = true
      }
    },

    onToggleOverrideBidExpiration(override) {
      this.$v.$touch()

      if (override) {
        this.bidPreferences.bidExpirationInSeconds = 60
      } else {
        this.bidPreferences.bidExpirationInSeconds = null
      }
    },

    onFormSubmit() {
      this.submitted = true

      if (this.isValid && this.rateLimitValid) {
        this.warningBidPreferencesChangeModal = true
      }
    },

    async onConfirmSubmit() {
      try {
        this.isLoading = true

        await this.updateSellerBidSettings({
          accountId: this.accountId,
          bidPreferences: this.bidPreferences
        })

        this.warningBidPreferencesChangeModal = false

        this.resetFormDirtyState()

        showSuccessMessage('Successfully changed bid preferences')
      } catch(e) {
        showErrorMessage(e.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    }
  },
}
</script>
<style>
  
</style>